import React, { useEffect, useState } from "react";
// Import Material-UI components for layout, loading, and animations
import { Box, Typography, CircularProgress, Fade } from "@mui/material";
// Import icons for visual emphasis
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FlashOnIcon from "@mui/icons-material/FlashOn";
// Import axios for making API requests
import axios from "axios";

// Define the UrgencyStrip component
const UrgencyStrip = () => {
  // Default urgency message
  const DEFAULT_MESSAGE =
    "Only 3 coaching spots available this week — Don't miss your chance.";

  // State to manage the urgency message
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  // State to manage loading status
  const [loading, setLoading] = useState(true);

  // Effect to fetch urgency message from API
  useEffect(() => {
    const fetchUrgencyMessage = async () => {
      try {
        // Fetch urgency message from API
        const response = await axios.get("/api/urgency");
        const data = response.data;

        // Update message based on API response
        if (data?.message) {
          setMessage(data.message);
        } else if (data?.spots) {
          setMessage(
            `Only ${data.spots} coaching spots available this week — Don't miss your chance.`
          );
        }
      } catch (error) {
        // Log error if API request fails
        console.error("UrgencyStrip API error:", error.message);
      } finally {
        // Set loading to false after request completes
        setLoading(false);
      }
    };

    // Execute the fetch function
    fetchUrgencyMessage();
  }, []);

  // Display loading state
  if (loading) {
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 2.5,
          px: 3,
          boxShadow: "0 -4px 20px rgba(255, 193, 7, 0.1)",
          borderTop: "1px solid rgba(255, 193, 7, 0.2)",
        }}
      >
        {/* Loading indicator */}
        <CircularProgress
          size={22}
          sx={{
            color: "#ffc107",
            mr: 2,
            filter: "drop-shadow(0 0 4px rgba(255, 193, 7, 0.3))",
          }}
        />
        <Typography
          sx={{
            fontSize: "0.9rem",
            fontWeight: 500,
            letterSpacing: "0.5px",
          }}
        >
          Loading...
        </Typography>
      </Box>
    );
  }

  // Main urgency strip content with fade-in animation
  return (
    <Fade in={!loading} timeout={800}>
      <Box
        sx={{
          background:
            "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: { xs: 2.5, sm: 3 },
          px: 3,
          position: "relative",
          bottom: 0,
          zIndex: -1000,
          textAlign: "center",
          flexWrap: "wrap",
          overflow: "hidden",
          borderTop: "2px solid transparent",
          borderImage:
            "linear-gradient(90deg, transparent, #ffc107, transparent) 1",
          boxShadow: `
            0 -8px 32px rgba(255, 193, 7, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.1)
          `,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: "-100%",
            width: "100%",
            height: "100%",
            background:
              "linear-gradient(90deg, transparent, rgba(255, 193, 7, 0.1), transparent)",
            animation: "shimmer 3s infinite",
          },
          "@keyframes shimmer": {
            "0%": { left: "-100%" },
            "100%": { left: "100%" },
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "1px",
            background:
              "linear-gradient(90deg, transparent 0%, #ffc107 50%, transparent 100%)",
            opacity: 0.6,
          },
        }}
      >
        {/* Animated pulse effect container */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              animation: "bounce 2s infinite",
              "@keyframes bounce": {
                "0%, 20%, 50%, 80%, 100%": { transform: "translateY(0)" },
                "40%": { transform: "translateY(-4px)" },
                "60%": { transform: "translateY(-2px)" },
              },
            }}
          >
            {/* Warning icon */}
            <WarningAmberIcon
              sx={{
                color: "#ffc107",
                mr: 0.5,
                fontSize: { xs: "1.3rem", sm: "1.6rem" },
                filter: "drop-shadow(0 0 8px rgba(255, 193, 7, 0.4))",
              }}
            />
            {/* Flash icon with animation */}
            <FlashOnIcon
              sx={{
                color: "#ff6b35",
                fontSize: { xs: "1.1rem", sm: "1.3rem" },
                filter: "drop-shadow(0 0 6px rgba(255, 107, 53, 0.4))",
                animation: "flash 1.5s infinite",
                "@keyframes flash": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                },
              }}
            />
          </Box>

          {/* Urgency message text */}
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.95rem", sm: "1.1rem" },
              color: "#fff",
              fontWeight: 600,
              letterSpacing: "0.3px",
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.8)",
              "& strong": {
                color: "#fff",
                backgroundClip: "text",
                filter: "drop-shadow(0 1px 2px rgba(255, 193, 7, 0.3))",
              },
            }}
          >
            <strong>{message}</strong>
          </Typography>
        </Box>
      </Box>
    </Fade>
  );
};

// Export the UrgencyStrip component
export default UrgencyStrip;